import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { MONO_FONT } from '@/theme';

/* Portada de un tip al estilo "Dispatch" de Bison, con CÓDIGO (no imagen):
   eyebrow mono + titular real (h3, crawlable) + radar + wordmark. Themed.
   `variant` replica los combos canónicos de marca usados en Instagram:
     ink   — fondo oscuro (DEEP INK)
     mint  — fondo teal claro (LIGHTEST)
     paper — fondo blanco
     teal  — teal sólido (BASE + WHITE)
   Todos dentro de la paleta (teal + neutros). aspectRatio 4/5. */

const VARIANTS = {
  ink: {
    bg: 'grey.950',
    eyebrow: 'primary.glow',
    text: 'common.white',
    accent: 'primary.glow',
    mark: 'primary.glow',
    markSub: 'grey.400',
    radar: 'primary.glow',
    radarOpacity: 0.45,
    border: null,
    // fondo oscuro → chip claro (icono teal)
    chipBg: 'common.white',
    chipFg: 'primary.main',
    chipBgHover: 'grey.100',
  },
  mint: {
    bg: 'primary.lightest',
    eyebrow: 'primary.darker',
    text: 'text.primary',
    accent: 'primary.darker',
    mark: 'primary.darker',
    markSub: 'grey.500',
    radar: 'primary.main',
    radarOpacity: 0.3,
    border: 'primary.light',
    // fondo claro → chip oscuro (icono blanco)
    chipBg: 'grey.950',
    chipFg: 'common.white',
    chipBgHover: 'grey.800',
  },
  paper: {
    bg: 'background.default',
    eyebrow: 'primary.dark',
    text: 'text.primary',
    accent: 'primary.darker',
    mark: 'primary.darker',
    markSub: 'grey.500',
    radar: 'primary.main',
    radarOpacity: 0.22,
    border: 'grey.200',
    // fondo claro → chip teal (icono blanco)
    chipBg: 'primary.main',
    chipFg: 'common.white',
    chipBgHover: 'primary.dark',
  },
  teal: {
    // Base 'primary.darker' (no 'primary.main'): el texto claro sobre #009f8b no llega a 4.5:1.
    bg: 'primary.darker',
    eyebrow: 'common.white',
    text: 'common.white',
    accent: 'primary.light',
    mark: 'common.white',
    markSub: 'rgba(255,255,255,0.85)',
    radar: 'common.white',
    radarOpacity: 0.4,
    border: null,
    // fondo teal sólido → chip light teal (icono dark teal)
    chipBg: 'primary.lightest',
    chipFg: 'primary.darker',
    chipBgHover: 'common.white',
  },
};

// Paleta de una variante (incl. colores del chip de IG). Fuente única: la usa
// TipCover y TipCard (para que el botón contraste con el fondo de cada portada).
export function tipVariant(variant) {
  return VARIANTS[variant] || VARIANTS.ink;
}

function Headline({ insight, accent }) {
  if (!accent || !insight.includes(accent)) return insight;
  const idx = insight.indexOf(accent);
  return (
    <>
      {insight.slice(0, idx)}
      <Box component="span" sx={{ color: 'inherit', opacity: 1 }} data-accent>
        {accent}
      </Box>
      {insight.slice(idx + accent.length)}
    </>
  );
}

export default function TipCover({
  insight,
  accent,
  dispatch,
  variant = 'ink',
}) {
  const v = tipVariant(variant);

  return (
    <Box
      sx={{
        position: 'relative',
        aspectRatio: '4 / 5',
        bgcolor: v.bg,
        color: v.text,
        borderRadius: '4px',
        overflow: 'hidden',
        ...(v.border && { border: '1px solid', borderColor: v.border }),
        p: { xs: '18px', md: '20px' },
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        // tiñe la palabra-acento del titular
        '& [data-accent]': { color: v.accent },
      }}
    >
      {/* Radar */}
      <Box
        component="svg"
        viewBox="0 0 200 200"
        aria-hidden="true"
        sx={{
          position: 'absolute',
          right: '-44px',
          bottom: '-36px',
          width: '72%',
          height: 'auto',
          color: v.radar,
          opacity: v.radarOpacity,
        }}
      >
        {[30, 56, 82, 108].map((r) => (
          <circle
            key={r}
            cx="120"
            cy="120"
            r={r}
            fill="none"
            stroke="currentColor"
            strokeWidth="1"
            opacity="0.35"
          />
        ))}
        <circle cx="120" cy="120" r="5" fill="currentColor" />
      </Box>

      {/* Eyebrow */}
      <Box
        sx={{
          position: 'relative',
          fontFamily: MONO_FONT,
          fontSize: '10px',
          letterSpacing: '0.16em',
          textTransform: 'uppercase',
          color: v.eyebrow,
          fontWeight: 600,
        }}
      >
        Bison · Dispatch {dispatch}
      </Box>

      {/* Titular */}
      <Typography
        lang="es"
        component="h3"
        sx={{
          position: 'relative',
          fontSize: 'clamp(19px, 1.7vw, 24px)',
          lineHeight: 1.18,
          letterSpacing: '-0.02em',
          color: v.text,
          fontWeight: 400,
          pr: '8%',
        }}
      >
        <Headline insight={insight} accent={accent} />
      </Typography>

      {/* Wordmark */}
      <Box
        sx={{
          position: 'relative',
          fontFamily: MONO_FONT,
          fontSize: '11px',
          letterSpacing: '0.02em',
        }}
      >
        <Box component="span" sx={{ color: v.mark, fontWeight: 700 }}>
          Bison
        </Box>{' '}
        <Box component="span" sx={{ color: v.markSub }}>
          agents
        </Box>
      </Box>
    </Box>
  );
}
